// Shared customer-detail drawer — GET /cards/:id, membership section for
// membership cards, and (for loyalty cards) a stamp summary + manual stamp
// correction + the revoke/erase danger zone. Used by both CustomersPage
// (search/recent list) and LogsPage (click a log row to jump straight to
// that customer) — customer lifecycle (2026-09-05).
//
// `onRecover` is optional: CustomersPage wires it to its own "recover pass"
// QR modal, LogsPage doesn't offer that action and just omits the button.

import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  useAdjustStamps,
  useEraseCustomerData,
  useRevokeCustomer,
  useUnrevokeCustomer,
} from '../hooks/useCustomers.js'
import { useCardDetail } from '../hooks/useMembership.js'
import { useStampHistory } from '../hooks/useStampHistory.js'
import { MembershipDetailSection } from './MembershipDetailSection.js'
import { LogRow } from './ActivityLogRow.js'
import {
  formatExpiry,
  relativeTimeAgo,
} from '../../shared/membership/membershipTypes.js'
import { OwnerApiError } from '../ownerApi.js'

/** Minimal shape either caller needs to provide — just enough to fetch and
 *  label the card. Everything else (stamps, total, customer state) comes
 *  from the GET /cards/:id fetch itself, so it can never drift from what a
 *  list/search row happened to carry at fetch time. */
export interface CustomerDetailRow {
  card_id: string
  card_code: string
  first_name: string | null
}

export function CustomerDetailModal({
  row,
  onClose,
  onRecover,
}: {
  row: CustomerDetailRow
  onClose: () => void
  onRecover?: () => void
}) {
  const detail = useCardDetail(row.card_id)
  const name = row.first_name?.trim() || 'Customer'
  const ctx = detail.data?.membership_context ?? null
  const customer = detail.data?.customer ?? null

  const [showAdjust, setShowAdjust] = useState(false)
  const [showRevoke, setShowRevoke] = useState(false)
  const [showErase, setShowErase] = useState(false)

  return (
    <div className="owner-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="owner-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${name} details`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="owner-modal-head">
          <h3>{name}</h3>
          <p className="owner-card-sub" style={{ margin: 0 }}>
            {row.card_code}
          </p>
        </div>

        {detail.isLoading && (
          <p className="owner-loading" style={{ marginTop: 16 }}>
            Loading…
          </p>
        )}
        {detail.isError && (
          <p className="owner-error" style={{ marginTop: 16 }}>
            Couldn't load this card. Try again.
          </p>
        )}

        {detail.isSuccess && ctx && (
          <MembershipDetailSection cardId={row.card_id} ctx={ctx} />
        )}

        {detail.isSuccess &&
          !ctx &&
          detail.data &&
          detail.data.card.total_stamps != null && (
            <div className="membership-detail-grid" style={{ marginTop: 12 }}>
              <div className="membership-detail-cell">
                <p className="membership-detail-label">Current progress</p>
                <p className="membership-detail-value">
                  {detail.data.card.stamps_count} / {detail.data.card.total_stamps} stamps
                </p>
              </div>
              {detail.data.secret_menu_adjustable?.enabled && (
                <div className="membership-detail-cell">
                  <p className="membership-detail-label">Secret-menu stamps</p>
                  <p className="membership-detail-value">
                    {detail.data.secret_menu_adjustable.required != null
                      ? `${detail.data.secret_menu_adjustable.count} of ${detail.data.secret_menu_adjustable.required}`
                      : detail.data.secret_menu_adjustable.count}
                  </p>
                </div>
              )}
              <div className="membership-detail-cell">
                <p className="membership-detail-label">Cards completed</p>
                <p className="membership-detail-value">
                  {detail.data.card.completions_count}
                </p>
              </div>
              <div className="membership-detail-cell">
                <p className="membership-detail-label">Stamps earned (lifetime)</p>
                <p className="membership-detail-value">
                  {detail.data.lifetime_stamps_count}
                </p>
              </div>
              <div className="membership-detail-cell">
                <p className="membership-detail-label">Last stamp</p>
                <p className="membership-detail-value">
                  {relativeTimeAgo(detail.data.last_stamp_at) ?? 'No stamps yet'}
                </p>
              </div>
              <div className="membership-detail-cell">
                <p className="membership-detail-label">Rewards claimed</p>
                <p className="membership-detail-value">
                  {detail.data.rewards_claimed_count}
                </p>
              </div>
              {detail.data.last_redemption && (
                <div className="membership-detail-cell">
                  <p className="membership-detail-label">Last reward</p>
                  <p className="membership-detail-value">
                    {detail.data.last_redemption.reward_text}
                    {' · '}
                    {relativeTimeAgo(detail.data.last_redemption.redeemed_at)}
                  </p>
                </div>
              )}
            </div>
          )}

        {detail.isSuccess &&
          !ctx &&
          detail.data &&
          detail.data.card.total_stamps != null && (
            <div style={{ marginTop: 8 }}>
              {!showAdjust && (
                <button
                  type="button"
                  className="owner-btn owner-btn-sm"
                  onClick={() => setShowAdjust(true)}
                >
                  Adjust stamps
                </button>
              )}
              {showAdjust && (
                <AdjustStampsForm
                  cardId={row.card_id}
                  currentCount={detail.data.card.stamps_count}
                  totalStamps={detail.data.card.total_stamps}
                  secretMenu={detail.data.secret_menu_adjustable}
                  onClose={() => setShowAdjust(false)}
                />
              )}
            </div>
          )}

        {detail.isSuccess && customer && (
          <div className="owner-danger-zone" style={{ marginTop: 20 }}>
            <p className="owner-danger-zone-heading">Danger zone</p>
            {customer.erased_at ? (
              <p className="owner-card-sub" style={{ margin: 0 }}>
                Data erased {relativeTimeAgo(customer.erased_at)}
                {customer.erased_by_name ? ` by ${customer.erased_by_name}` : ''}.
              </p>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {customer.deactivated_at ? (
                  <UnrevokeButton customerId={customer.id} cardId={row.card_id} />
                ) : (
                  <button
                    type="button"
                    className="owner-btn owner-btn-danger owner-btn-sm"
                    onClick={() => setShowRevoke(true)}
                  >
                    Revoke customer
                  </button>
                )}
                <button
                  type="button"
                  className="owner-btn owner-btn-danger owner-btn-sm"
                  onClick={() => setShowErase(true)}
                >
                  Erase customer data
                </button>
              </div>
            )}
            {customer.deactivated_at && !customer.erased_at && (
              <p className="owner-card-sub" style={{ marginTop: 6, marginBottom: 0 }}>
                Revoked {relativeTimeAgo(customer.deactivated_at)}
                {customer.revoked_by_name ? ` by ${customer.revoked_by_name}` : ''}
                {customer.deactivation_reason
                  ? ` · ${customer.deactivation_reason}`
                  : ''}
              </p>
            )}
          </div>
        )}

        {detail.isSuccess &&
          !ctx &&
          detail.data &&
          detail.data.card.total_stamps != null &&
          customer && (
            <CustomerActivitySection
              customerId={customer.id}
              enrolledAt={customer.created_at}
            />
          )}

        <div className="owner-form-actions" style={{ marginTop: 16 }}>
          {/* Customer lifecycle (2026-09-07) — same erased-hides-recover
              rule as the Customers list row (see CustomersPage). */}
          {onRecover && !customer?.erased_at && (
            <button type="button" className="owner-btn" onClick={onRecover}>
              Recover pass
            </button>
          )}
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>

      {showRevoke && customer && (
        <RevokeConfirmModal
          customerId={customer.id}
          cardId={row.card_id}
          name={name}
          onClose={() => setShowRevoke(false)}
        />
      )}
      {showErase && customer && (
        <EraseConfirmModal
          customerId={customer.id}
          cardId={row.card_id}
          name={name}
          onClose={() => setShowErase(false)}
        />
      )}
    </div>
  )
}

// Customer lifecycle (2026-09-05) — this customer's full stamp/redemption/
// correction history, oldest-last (same order as Logs), with a synthetic
// "Customer enrolled" entry appended once the real history is exhausted so
// the timeline reads start-to-latest. Card codes/customer names are omitted
// from each row (hideCustomer) since every row here is already this
// customer's — see LogRow. Revoke/erase aren't part of this feed; their
// current state is the danger-zone block above (only the latest revoke is
// kept, not a full history — see naya-pass memory customer-lifecycle-feature
// if that ever needs to change).
function CustomerActivitySection({
  customerId,
  enrolledAt,
}: {
  customerId: string
  enrolledAt: string
}) {
  const history = useStampHistory('all', undefined, 'all', '', customerId)
  const items = history.data?.pages.flatMap((p) => p.items) ?? []
  const reachedStart = history.isSuccess && !history.hasNextPage

  return (
    <div style={{ marginTop: 20 }}>
      <p className="owner-danger-zone-heading" style={{ color: 'inherit' }}>
        Activity
      </p>

      {history.isLoading && (
        <p className="owner-loading" style={{ margin: 0 }}>
          Loading activity…
        </p>
      )}
      {history.isError && (
        <p className="owner-field-error" role="alert">
          Couldn't load this customer's activity. Try again.
        </p>
      )}

      {history.isSuccess && (
        <ol className="logs-list" aria-label="Customer activity">
          {items.map((item) => (
            <LogRow key={item.id} item={item} onOpen={() => {}} hideCustomer />
          ))}
          {reachedStart && (
            <li className="logs-row">
              <div className="logs-row-main">
                <div className="logs-row-customer">Customer enrolled</div>
                <div className="logs-row-meta">{formatExpiry(enrolledAt)}</div>
              </div>
            </li>
          )}
        </ol>
      )}

      {history.hasNextPage && (
        <button
          type="button"
          className="owner-btn owner-btn-block logs-load-more"
          onClick={() => void history.fetchNextPage()}
          disabled={history.isFetchingNextPage}
        >
          {history.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}

function AdjustStampsForm({
  cardId,
  currentCount,
  totalStamps,
  secretMenu,
  onClose,
}: {
  cardId: string
  currentCount: number
  totalStamps: number
  /** Null/disabled hides the secret-stamp stepper entirely — a program
   *  without secret-menu tagging on has nothing to correct there. */
  secretMenu: { enabled: boolean; count: number; tags: boolean[] } | null
  onClose: () => void
}) {
  // Both default to "no change" (current → current) — the owner must
  // deliberately click +/- before Apply does anything. A pre-filled -1 read
  // as an already-staged change nobody asked for.
  const [delta, setDelta] = useState(0)
  const [secretDelta, setSecretDelta] = useState(0)
  const [reason, setReason] = useState('')
  const adjust = useAdjustStamps()

  // Clamped exactly like the real stamping stepper (StampOverlayChrome):
  // the buttons themselves refuse to go out of range, so there's nothing
  // left to reject server-side.
  const resultCount = currentCount + delta
  const canDecrease = resultCount > 0
  const canIncrease = resultCount < totalStamps

  const secretEnabled = secretMenu?.enabled ?? false
  const tags = secretMenu?.tags ?? []
  // A DECREASE deletes the highest stamp_number rows first — which may
  // themselves be secret-tagged — so the secret baseline isn't static
  // "current count", it's "how many of the rows that would SURVIVE this
  // total-count change are tagged secret". tags is ordered by stamp_number
  // ascending, so the surviving rows are simply its first `resultCount`
  // entries. An increase never removes anything, so the baseline is
  // unaffected by delta in that direction.
  const secretBase =
    delta < 0
      ? tags.slice(0, Math.max(0, resultCount)).filter(Boolean).length
      : (secretMenu?.count ?? 0)

  // Re-clamp secretDelta whenever the main stepper moves the baseline out
  // from under it — same pattern as StampOverlayChrome's secretCount effect.
  useEffect(() => {
    setSecretDelta((d) => Math.max(-secretBase, Math.min(resultCount - secretBase, d)))
  }, [secretBase, resultCount])

  const secretResult = secretBase + secretDelta
  const canDecreaseSecret = secretResult > 0
  const canIncreaseSecret = secretResult < resultCount

  const nothingToDo = delta === 0 && secretDelta === 0

  function apply() {
    adjust.mutate(
      { cardId, delta, secretDelta: secretEnabled ? secretDelta : 0, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('Stamp count adjusted.')
          onClose()
        },
        onError: (err) => {
          const msg =
            err instanceof OwnerApiError ? err.userMessage : 'Could not adjust stamps.'
          toast.error(msg)
        },
      },
    )
  }

  return (
    <div className="owner-card" style={{ marginTop: 8 }}>
      <div className="owner-adjust-section">
        <p className="membership-detail-label">Total stamps</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={() => setDelta((d) => d - 1)}
            disabled={!canDecrease}
            aria-label="Decrease stamps"
          >
            −
          </button>
          <span style={{ minWidth: 90, textAlign: 'center' }}>
            {currentCount} → {resultCount}
          </span>
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={() => setDelta((d) => d + 1)}
            disabled={!canIncrease}
            aria-label="Increase stamps"
          >
            +
          </button>
        </div>
      </div>

      {secretEnabled && (
        <div className="owner-adjust-section owner-adjust-section-secret">
          <p className="membership-detail-label">Secret-menu stamps</p>
          <p className="owner-card-sub" style={{ margin: '2px 0 6px' }}>
            How many of the total above count as off-menu orders — not an
            additional stamp count.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              className="owner-btn owner-btn-sm"
              onClick={() => setSecretDelta((d) => d - 1)}
              disabled={!canDecreaseSecret}
              aria-label="Decrease secret-menu stamps"
            >
              −
            </button>
            <span style={{ minWidth: 90, textAlign: 'center' }}>
              {secretBase} → {secretResult}
            </span>
            <button
              type="button"
              className="owner-btn owner-btn-sm"
              onClick={() => setSecretDelta((d) => d + 1)}
              disabled={!canIncreaseSecret}
              aria-label="Increase secret-menu stamps"
            >
              +
            </button>
          </div>
        </div>
      )}

      <div className="owner-field" style={{ marginTop: 10 }}>
        <label className="owner-label" htmlFor="adjust-reason">
          Reason
        </label>
        <textarea
          id="adjust-reason"
          className="owner-textarea"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Accidentally scanned twice"
        />
      </div>
      <div className="owner-form-actions" style={{ marginTop: 10 }}>
        <button type="button" className="owner-btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={apply}
          disabled={adjust.isPending || nothingToDo || !reason.trim()}
        >
          {adjust.isPending ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </div>
  )
}

function UnrevokeButton({ customerId, cardId }: { customerId: string; cardId: string }) {
  const unrevoke = useUnrevokeCustomer()
  function handle() {
    unrevoke.mutate(
      { customerId, cardId },
      {
        onSuccess: () => toast.success('Customer unrevoked.'),
        onError: (err) => {
          const msg =
            err instanceof OwnerApiError ? err.userMessage : 'Could not unrevoke.'
          toast.error(msg)
        },
      },
    )
  }
  return (
    <button
      type="button"
      className="owner-btn owner-btn-sm"
      onClick={handle}
      disabled={unrevoke.isPending}
    >
      {unrevoke.isPending ? 'Unrevoking…' : 'Unrevoke customer'}
    </button>
  )
}

function RevokeConfirmModal({
  customerId,
  cardId,
  name,
  onClose,
}: {
  customerId: string
  cardId: string
  name: string
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const revoke = useRevokeCustomer()

  function confirm() {
    revoke.mutate(
      { customerId, cardId, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${name} was revoked.`)
          onClose()
        },
        onError: (err) => {
          const msg =
            err instanceof OwnerApiError ? err.userMessage : 'Could not revoke customer.'
          toast.error(msg)
        },
      },
    )
  }

  return (
    <div className="owner-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="owner-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Revoke customer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="owner-modal-head">
          <h3>Revoke {name}?</h3>
          <p className="owner-card-sub" style={{ margin: 0 }}>
            They'll stop earning new stamps. Their card and history stay
            intact — you can unrevoke them anytime.
          </p>
        </div>
        <div className="owner-field" style={{ marginTop: 12 }}>
          <label className="owner-label" htmlFor="revoke-reason">
            Reason (required)
          </label>
          <textarea
            id="revoke-reason"
            className="owner-textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="owner-form-actions" style={{ marginTop: 12 }}>
          <button type="button" className="owner-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-danger"
            onClick={confirm}
            disabled={revoke.isPending || !reason.trim()}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke customer'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EraseConfirmModal({
  customerId,
  cardId,
  name,
  onClose,
}: {
  customerId: string
  cardId: string
  name: string
  onClose: () => void
}) {
  const [requestedVia, setRequestedVia] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const erase = useEraseCustomerData()
  const canConfirm = confirmText.trim() === 'DELETE' && requestedVia.trim().length > 0

  function confirm() {
    erase.mutate(
      { customerId, cardId, requestedVia: requestedVia.trim() },
      {
        onSuccess: () => {
          toast.success(`${name}'s data was erased.`)
          onClose()
        },
        onError: (err) => {
          const msg =
            err instanceof OwnerApiError ? err.userMessage : 'Could not erase customer data.'
          toast.error(msg)
        },
      },
    )
  }

  return (
    <div className="owner-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="owner-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Erase customer data"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="owner-modal-head">
          <h3>Erase {name}'s data?</h3>
          <p className="owner-card-sub" style={{ margin: 0 }}>
            This permanently removes their name and personal details. This
            cannot be undone. Use this only for an actual customer data
            request — for a routine block, use Revoke instead.
          </p>
        </div>
        <div className="owner-field" style={{ marginTop: 12 }}>
          <label className="owner-label" htmlFor="erase-requested-via">
            How was this requested? (required)
          </label>
          <input
            id="erase-requested-via"
            className="owner-input"
            value={requestedVia}
            onChange={(e) => setRequestedVia(e.target.value)}
            placeholder="e.g. In person, Messenger"
          />
        </div>
        <div className="owner-field" style={{ marginTop: 12 }}>
          <label className="owner-label" htmlFor="erase-confirm-text">
            Type DELETE to confirm
          </label>
          <input
            id="erase-confirm-text"
            className="owner-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="owner-form-actions" style={{ marginTop: 12 }}>
          <button type="button" className="owner-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-danger"
            onClick={confirm}
            disabled={erase.isPending || !canConfirm}
          >
            {erase.isPending ? 'Erasing…' : 'Erase data'}
          </button>
        </div>
      </div>
    </div>
  )
}
